import { ISignedScopeDocument } from "@/api";
import { Box, CircularProgress } from "@mui/material";
import type { PageLayout } from "@react-pdf-viewer/core";
import { Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { highlightPlugin } from "@react-pdf-viewer/highlight";
import "@react-pdf-viewer/highlight/lib/styles/index.css";
import { PageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { useSearchParams } from "react-router";
import {
    FoundToggleState,
    IGroupedScopeClause,
    ScopeDetailsTab,
} from "../../types";
import { ClauseHighlights } from "./ClauseHighlights";
import { ViewerToolbar } from "./ViewerToolbar";

interface IProps {
    /**
     * The scope to display
     */
    signedScopeDocument: ISignedScopeDocument | undefined;
    signedScopeDocumentLoading: boolean;
    /**
     * The grouped scope clauses
     */
    groupedScopeClauses: IGroupedScopeClause[];
    /**
     * Set the expanded found clause
     */
    setExpandedFoundClause: (expanded: string | false) => void;
    /**
     * The currently expanded/selected clause panel ID
     */
    expandedFoundClause: string | false;
    /**
     * The page navigation plugin instance
     */
    pageNavigationPluginInstance: PageNavigationPlugin;
    /**
     * Set the pdf viewer loaded state
     */
    setPdfViewerLoaded: (loaded: boolean) => void;
    /**
     * Called when the visible page changes (0-indexed from viewer, caller should convert)
     */
    onPageChange?: (pageIndex: number) => void;
    /**
     * Called once on document load with the total page count
     */
    onTotalPages?: (totalPages: number) => void;
    setTab: (tab: ScopeDetailsTab) => void;
    setClausesFoundToggle: (toggle: FoundToggleState) => void;
}

/**
 * A PDF viewer for a scope document
 */
export const PdfViewer = ({
    signedScopeDocument,
    signedScopeDocumentLoading,
    groupedScopeClauses,
    setExpandedFoundClause,
    expandedFoundClause,
    pageNavigationPluginInstance,
    setPdfViewerLoaded,
    onPageChange,
    onTotalPages,
    setTab,
    setClausesFoundToggle,
}: IProps) => {
    const [searchParams] = useSearchParams();

    const initialPage = searchParams.get("page")
        ? parseInt(searchParams.get("page")!) - 1
        : 0;

    // render custom toolbar
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        renderToolbar: ViewerToolbar,
        sidebarTabs: () => [],
    });

    // render highlights
    const highlightPluginInstance = highlightPlugin({
        renderHighlights: (props) => (
            <ClauseHighlights
                {...props}
                groupedScopeClauses={groupedScopeClauses}
                setExpandedFoundClause={setExpandedFoundClause}
                expandedFoundClause={expandedFoundClause}
                setTab={setTab}
                setClausesFoundToggle={setClausesFoundToggle}
            />
        ),
    });

    if (signedScopeDocumentLoading)
        return (
            <Box
                width={"100%"}
                height={"100%"}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
            >
                <CircularProgress />
            </Box>
        );

    if (!signedScopeDocument) throw new Error("Document not found");

    // adds bottom margin to the last page of the document
    // necessary so that the mobile bottom sheet doesn't overlap with the last page
    const pageLayout: PageLayout = {
        transformSize: ({ numPages, pageIndex, size }) => ({
            height: pageIndex + 1 === numPages ? size.height + 50 : size.height,
            width: size.width,
        }),
    };

    return (
        <Box width={"100%"} maxWidth={"md"} height={"100%"}>
            <Viewer
                plugins={[
                    defaultLayoutPluginInstance,
                    highlightPluginInstance,
                    pageNavigationPluginInstance,
                ]}
                fileUrl={signedScopeDocument.signed_url}
                pageLayout={pageLayout}
                onDocumentLoad={(e) => {
                    onTotalPages?.(e.doc.numPages);
                    setTimeout(() => {
                        setPdfViewerLoaded(true);
                    }, 500);
                }}
                onPageChange={(e) => {
                    onPageChange?.(e.currentPage);
                }}
                initialPage={initialPage}
            />
        </Box>
    );
};
